import { Link, useNavigate } from '@tanstack/react-router';
import { useEffect, useState } from 'react';

import { useAuthStore } from '@/features/auth';
import DevLogin from '@/pages/login/DevLogin';
import { cn } from '@/shared/lib/utils';
import { ThemeToggle } from '@/shared/ui/ThemeToggle';

// ─── 아이콘 ─────────────────────────────────────────────────────────────────

type IconKind =
  | 'calendar'
  | 'users'
  | 'user'
  | 'login'
  | 'logout'
  | 'shield'
  | 'menu'
  | 'close';

function Icon({ kind, size = 14 }: { kind: IconKind; size?: number }) {
  const props = {
    width: size,
    height: size,
    viewBox: '0 0 16 16',
    fill: 'none',
    stroke: 'currentColor',
    strokeWidth: 1.6,
    strokeLinecap: 'round' as const,
    strokeLinejoin: 'round' as const,
  };
  switch (kind) {
    case 'calendar':
      return (
        <svg {...props}>
          <rect x='2.5' y='3.5' width='11' height='10' rx='1.5' />
          <path d='M5 2v3M11 2v3M2.5 7h11' />
        </svg>
      );
    case 'users':
      return (
        <svg {...props}>
          <circle cx='6' cy='6' r='2.2' />
          <path d='M2 13c.5-2 2-3 4-3s3.5 1 4 3' />
          <path d='M11 6.5c1 0 1.7.7 1.7 1.7s-.7 1.7-1.7 1.7' />
          <path d='M14 13c-.3-1.3-1.2-2-2.5-2.3' />
        </svg>
      );
    case 'user':
      return (
        <svg {...props}>
          <circle cx='8' cy='6' r='2.5' />
          <path d='M3 13.5c.7-2.3 2.7-3.5 5-3.5s4.3 1.2 5 3.5' />
        </svg>
      );
    case 'login':
      return (
        <svg {...props}>
          <path d='M9.5 3.5h3v9h-3' />
          <path d='M2 8h7m0 0L7 6m2 2L7 10' />
        </svg>
      );
    case 'logout':
      return (
        <svg {...props}>
          <path d='M6.5 3.5h-3v9h3' />
          <path d='M14 8H6m0 0l2-2m-2 2l2 2' />
        </svg>
      );
    case 'shield':
      return (
        <svg {...props}>
          <path d='M8 1.5l5.5 2v4.5C13.5 11.5 11 13.5 8 14.5c-3-1-5.5-3-5.5-6.5V3.5l5.5-2z' />
        </svg>
      );
    case 'menu':
      return (
        <svg {...props}>
          <path d='M2.5 4h11M2.5 8h11M2.5 12h11' />
        </svg>
      );
    case 'close':
      return (
        <svg {...props}>
          <path d='M3.5 3.5l9 9M12.5 3.5l-9 9' />
        </svg>
      );
    default:
      return null;
  }
}

// ─── 메인 ───────────────────────────────────────────────────────────────────

export default function Header({ className = '' }: { className?: string }) {
  const isLoggedIn = useAuthStore((state) => !!state.member);
  const logout = useAuthStore((state) => state.logout);
  const isAdmin = useAuthStore((state) =>
    state.member?.roles?.includes('ADMIN'),
  );
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);

  const handleLogout = () => {
    logout();
    navigate({ to: '/' });
    setMobileOpen(false);
  };

  const closeMobile = () => setMobileOpen(false);

  // mobile open 시 body scroll lock
  useEffect(() => {
    if (mobileOpen) {
      const prev = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      return () => {
        document.body.style.overflow = prev;
      };
    }
  }, [mobileOpen]);

  return (
    <>
      <header
        className={cn(
          'fixed top-0 left-0 z-50 h-14 w-full md:h-16',
          className,
        )}
        style={{
          background: 'var(--bs-glass)',
          backdropFilter: 'saturate(180%) blur(20px)',
          WebkitBackdropFilter: 'saturate(180%) blur(20px)',
          borderBottom: '1px solid var(--bs-line-soft)',
        }}
      >
        <div className='bs-container bs-header-row flex h-full items-center gap-4'>
          {/* 로고 — Fraunces italic */}
          <Link
            to='/'
            className='flex-shrink-0 text-[20px] leading-none font-semibold italic md:text-[22px]'
            style={{
              fontFamily: 'var(--bs-font-serif)',
              color: 'var(--bs-ink)',
              letterSpacing: '-0.025em',
            }}
            onClick={closeMobile}
          >
            Before <span style={{ color: 'var(--bs-accent)' }}>Somerise</span>
          </Link>

          {/* 관리자 메뉴 (PC) */}
          {isAdmin && (
            <div
              className='bs-header-admin ml-2 hidden items-center gap-0.5 rounded-lg border px-2 py-1 md:flex'
              style={{
                background: 'var(--bs-paper-alt)',
                borderColor: 'var(--bs-line)',
              }}
            >
              <Link
                to='/admin/meetup'
                className='bs-header-link inline-flex items-center gap-1.5 rounded-md px-2.5 py-1.5 text-[13px] font-medium whitespace-nowrap'
                style={{ color: 'var(--bs-ink-soft)' }}
              >
                <Icon kind='calendar' /> 소개팅 관리
              </Link>
              <Link
                to='/admin/booking'
                className='bs-header-link inline-flex items-center gap-1.5 rounded-md px-2.5 py-1.5 text-[13px] font-medium whitespace-nowrap'
                style={{ color: 'var(--bs-ink-soft)' }}
              >
                <Icon kind='users' /> 예약 관리
              </Link>
            </div>
          )}

          {/* 스페이서 */}
          <div className='flex-1' />

          {/* PC 메뉴 */}
          <nav className='bs-header-nav hidden items-center gap-1.5 md:flex'>
            {import.meta.env.DEV && <DevLogin />}

            <Link
              to='/about'
              className='bs-header-link inline-flex items-center gap-1.5 rounded-md px-2.5 py-1.5 text-[13px] font-medium whitespace-nowrap'
              style={{ color: 'var(--bs-ink-soft)' }}
            >
              이용 안내
            </Link>
            <Link
              to='/about/location'
              className='bs-header-link inline-flex items-center gap-1.5 rounded-md px-2.5 py-1.5 text-[13px] font-medium whitespace-nowrap'
              style={{ color: 'var(--bs-ink-soft)' }}
            >
              오시는 길
            </Link>
            <ThemeToggle />
            <Link to='/booking' className='bs-btn bs-btn-primary ml-1'>
              참여 신청
              <svg
                width='13'
                height='13'
                viewBox='0 0 16 16'
                fill='none'
                stroke='currentColor'
                strokeWidth='2'
                strokeLinecap='round'
                strokeLinejoin='round'
              >
                <path d='M3 8h10M9 4l4 4-4 4' />
              </svg>
            </Link>

            {isLoggedIn ? (
              <>
                <Link
                  to='/my-page'
                  className='bs-header-link inline-flex items-center gap-1.5 rounded-md px-2.5 py-1.5 text-[13px] font-medium whitespace-nowrap'
                  style={{ color: 'var(--bs-ink-soft)' }}
                >
                  <Icon kind='user' /> 내 정보
                </Link>
                <button
                  type='button'
                  onClick={handleLogout}
                  className='bs-header-link inline-flex items-center gap-1.5 rounded-md border px-3 py-1.5 text-[13px] font-medium whitespace-nowrap'
                  style={{
                    background: 'var(--bs-paper)',
                    borderColor: 'var(--bs-line)',
                    color: 'var(--bs-ink-soft)',
                  }}
                >
                  <Icon kind='logout' /> 로그아웃
                </button>
              </>
            ) : (
              <Link
                to='/sign-in'
                className='bs-header-link inline-flex items-center gap-1.5 rounded-md border px-3 py-1.5 text-[13px] font-medium whitespace-nowrap'
                style={{
                  background: 'var(--bs-paper)',
                  borderColor: 'var(--bs-line)',
                  color: 'var(--bs-ink-soft)',
                }}
              >
                <Icon kind='login' /> 로그인
              </Link>
            )}
          </nav>

          {/* 모바일 햄버거 */}
          <button
            type='button'
            onClick={() => setMobileOpen((v) => !v)}
            aria-label='메뉴 열기'
            className='flex h-10 w-10 items-center justify-center rounded-md md:hidden'
            style={{ color: 'var(--bs-ink)' }}
          >
            <Icon kind={mobileOpen ? 'close' : 'menu'} size={18} />
          </button>
        </div>
      </header>

      {/* 모바일 드롭다운 */}
      <div
        className={cn(
          'fixed inset-x-0 top-14 z-40 transition-all duration-300 md:hidden',
          mobileOpen
            ? 'translate-y-0 opacity-100'
            : 'pointer-events-none -translate-y-2 opacity-0',
        )}
        style={{
          background: 'var(--bs-paper)',
          borderBottom: '1px solid var(--bs-line)',
          maxHeight: 'calc(100vh - 56px)',
          overflowY: 'auto',
        }}
      >
        <div className='space-y-3 px-4 py-4'>
          {/* 관리자 메뉴 */}
          {isAdmin && (
            <div
              className='rounded-lg border p-3'
              style={{
                background: 'var(--bs-paper-alt)',
                borderColor: 'var(--bs-line)',
              }}
            >
              <div
                className='mb-2 flex items-center gap-1.5 text-[11px] font-bold uppercase'
                style={{
                  color: 'var(--bs-accent)',
                  letterSpacing: '0.06em',
                }}
              >
                <Icon kind='shield' size={12} />
                <span>관리자 메뉴</span>
                <span
                  className='ml-auto rounded px-1.5 py-0.5 text-[9px]'
                  style={{
                    background: 'color-mix(in oklab, var(--bs-accent) 12%, transparent)',
                  }}
                >
                  ADMIN
                </span>
              </div>
              <Link
                to='/admin/meetup'
                onClick={closeMobile}
                className='flex items-center gap-2 rounded px-1 py-2.5 text-[15px]'
                style={{ color: 'var(--bs-ink)' }}
              >
                <Icon kind='calendar' /> 소개팅 관리
              </Link>
              <Link
                to='/admin/booking'
                onClick={closeMobile}
                className='flex items-center gap-2 rounded px-1 py-2.5 text-[15px]'
                style={{ color: 'var(--bs-ink)' }}
              >
                <Icon kind='users' /> 예약 관리
              </Link>
            </div>
          )}

          {/* 메뉴 */}
          <Link
            to='/about'
            onClick={closeMobile}
            className='block rounded px-1 py-3 text-[15px] font-medium'
            style={{ color: 'var(--bs-ink)' }}
          >
            이용 안내
          </Link>
          <Link
            to='/about/location'
            onClick={closeMobile}
            className='block rounded px-1 py-3 text-[15px] font-medium'
            style={{ color: 'var(--bs-ink)' }}
          >
            오시는 길
          </Link>

          {/* 테마 토글 — 모바일 드롭다운 */}
          <div
            className='flex items-center justify-between rounded px-1 py-2 text-[15px] font-medium'
            style={{ color: 'var(--bs-ink)' }}
          >
            <span>화면 모드</span>
            <ThemeToggle />
          </div>

          <Link
            to='/booking'
            onClick={closeMobile}
            className='bs-btn bs-btn-primary w-full justify-center'
          >
            참여 신청
          </Link>

          {isLoggedIn ? (
            <>
              <Link
                to='/my-page'
                onClick={closeMobile}
                className='flex items-center gap-2 rounded px-1 py-3 text-[15px] font-medium'
                style={{ color: 'var(--bs-ink)' }}
              >
                <Icon kind='user' /> 내 정보
              </Link>
              <button
                type='button'
                onClick={handleLogout}
                className='flex w-full items-center gap-2 rounded px-1 py-3 text-left text-[15px] font-medium'
                style={{ color: 'var(--bs-ink-soft)' }}
              >
                <Icon kind='logout' /> 로그아웃
              </button>
            </>
          ) : (
            <Link
              to='/sign-in'
              onClick={closeMobile}
              className='flex items-center gap-2 rounded px-1 py-3 text-[15px] font-medium'
              style={{ color: 'var(--bs-ink)' }}
            >
              <Icon kind='login' /> 로그인
            </Link>
          )}
        </div>
      </div>

      <style>{`
        .bs-header-link {
          transition:
            color var(--bs-dur-fast) var(--bs-ease),
            background var(--bs-dur-fast) var(--bs-ease);
        }
        .bs-header-link:hover {
          color: var(--bs-accent) !important;
          background: var(--bs-paper-alt);
        }
      `}</style>
    </>
  );
}
